import { ControllerSaveFunction } from "csi/middleware/common";
import { submitWriter } from "csi/service/writerService";
import { C8Node, cnl, ICache } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { action, makeObservable, observable } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import csi from "csi/main";

/** Classes with Bypass Reason action */
const FLAT_INGREDIENT_ITEM = "FlatIngredientItem";
const FOOD_INGREDIENT = "FoodIngredient";

/** FlatIngredientItem and FoodIngredient's Bypass Reason attributes */
const DEFAULT_PRODUCT_COMPLIANCE_ANALYSIS_BYPASS_REASON = "DefaultProductComplianceAnalysisBypassReason";
const PRODUCT_COMPLIANCE_ANALYSIS_BYPASS_REASON = "ProductComplianceAnalysisBypassReason";

export default class ComplianceAnalysisBypassPopupStore extends ReactionStore {
    @observable bypassToggle: boolean;
    @observable bypassReason: string;
    private readonly cache: ICache;
    private readonly currentNodeCNL: cnl;
    private readonly _onSave: ControllerSaveFunction;
    readonly titleRes: string;
    private readonly writer: Writer;

    /** indicate if bypass reason is null or blank */
    get bypassReasonIsBlank(): boolean {
    	return !this.bypassReason || this.bypassReason.trim().length === 0;
    }

    /** returns the current node */
    get currentItemNode(): C8Node {
    	return this.cache.get(this.currentNodeCNL);
    }

    /** indicate if the current node has no library item and bypass reason is blank */
    get hasNoLibraryItemAndBlankBypassReasonField(): boolean {
    	return this.currentItemNode.LibraryItem === csi.EMPTY_URL && this.bypassReasonIsBlank;
    }

    /** indicate if the current node has no compliance system ID and bypass reason is blank */
    get hasNoComplianceSystemIDAndBlankBypassReasonField(): boolean {
    	return this.hasNoComplianceSystemID && this.bypassReasonIsBlank;
    }

    /** indicate if the current node has product compliance analysis reason and bypass reason is blank */
    get hasProdCompAnalysisReasonAndBlankBypassReason(): boolean {
    	return this.currentItemNode.LibraryItem !== csi.EMPTY_URL && this.currentItemNode.ProductComplianceAnalysisBypassReason !== csi.EMPTY_STRING && this.bypassReasonIsBlank;
    }

    /** indiciate if the current node has no library item */
    get hasNoLibraryItem(): boolean {
    	return this.currentItemNode.LibraryItem === csi.EMPTY_URL;
    }

    /** indicate if the current node has product compliance analysis reason */
    get hasProdCompAnalysisReason(): boolean {
    	return this.currentItemNode.ProductComplianceAnalysisBypassReason !== csi.EMPTY_STRING;
    }

    /** indicate if the current node has no compliance system ID */
    get hasNoComplianceSystemID(): boolean {
    	const libraryItemURL = this.currentItemNode.LibraryItem;

    	if (libraryItemURL !== csi.EMPTY_URL) {
    		const libraryItemNodes = this.cache.getNodes([libraryItemURL]);
    		const complianceSystemID = libraryItemNodes[0]?.ComplianceSystemID;
	
    		return complianceSystemID === csi.EMPTY_STRING;
    	}
    }

    /** indicate if save button can be enabled based on toggle and bypass reason value */
    get isSaveButtonEnabled(): boolean {
    	if (this.hasNoLibraryItemAndBlankBypassReasonField || this.hasNoComplianceSystemIDAndBlankBypassReasonField) {
    		return false;
    	}
    	return (this.bypassToggle && !this.bypassReasonIsBlank) || !this.bypassToggle;
    }

    /** returns the current node's type */
    get nodeType(): string {
    	return this.currentItemNode.$Type;
    }

    /** set bypass reason based on textfield value */
    @action setBypassReason = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    	this.bypassReason = event.target.value;
    };

    /** indicate if the current node has product compliance analysis reason and library item */
    get hasProdCompAnalysisIDAndLibraryItem(): boolean {
    	return this.currentItemNode.LibraryItem !== csi.EMPTY_URL && this.currentItemNode.ComplianceSystemID !== csi.EMPTY_STRING;
    }

    /** set bypass toggle based on bypass switch value */
    @action setBypassToggle = (event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    	if (this.hasNoLibraryItem || this.hasNoComplianceSystemID) {
    		this.bypassToggle = true;
    		return;
    	}
		
    	this.bypassToggle = checked;
    	if (!this.bypassToggle) {
    		this.bypassReason = "";
    	}
    };

    onSave = async (): Promise<boolean> => {
    	const cn = this.writer.changeNode(this.currentItemNode.$URL);
		
    	const trimmedBypassReason = this.bypassReason.trim();
    	if (this.nodeType === FLAT_INGREDIENT_ITEM) {
    		cn.changeAttribute(PRODUCT_COMPLIANCE_ANALYSIS_BYPASS_REASON, xml.ATTR_STRING, trimmedBypassReason);
    	} else if (this.nodeType === FOOD_INGREDIENT) {
    		cn.changeAttribute(DEFAULT_PRODUCT_COMPLIANCE_ANALYSIS_BYPASS_REASON, xml.ATTR_STRING, trimmedBypassReason);
    	}

    	this._onSave(submitWriter(this.writer));
		
    	return true;
    };

    constructor(actionId: string, writer: Writer, currentNodeCNL: cnl, cache: ICache, onSave: ControllerSaveFunction) {
    	super();
    	makeObservable(this);

    	this.cache = cache;
    	this.currentNodeCNL = currentNodeCNL;
    	this.writer = writer;
    	this.titleRes = actionId;

    	if (this.nodeType === FLAT_INGREDIENT_ITEM) {
    		this.bypassReason = this.currentItemNode.ProductComplianceAnalysisBypassReason;
    	} else if (this.nodeType === FOOD_INGREDIENT) {
    		this.bypassReason = this.currentItemNode.DefaultProductComplianceAnalysisBypassReason;
    	}

    	this.bypassToggle = !this.bypassReasonIsBlank;
    	this._onSave = onSave;
    }
}

export const ComplianceAnalysisBypassPopupStoreContext = createContext<ComplianceAnalysisBypassPopupStore>(undefined);
export const useComplianceAnalysisBypassPopupStore = (): ComplianceAnalysisBypassPopupStore => useContext(ComplianceAnalysisBypassPopupStoreContext);